import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { EquipmentPicker, composeEquipment } from '@/components/EquipmentPicker';
import { resolveUnits, windUnit, windToKnots, windPlaceholder } from '@/lib/units';
import { UploadCloud, Film, Loader2, Wind, Sparkles, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const DIRS = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks — bypasses proxy/ingress body-size limits

export default function Upload() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const units = resolveUnits(user);
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [spots, setSpots] = useState([]);
  const [spotId, setSpotId] = useState('');
  const [wind, setWind] = useState('');
  const [dir, setDir] = useState('');
  const [gear, setGear] = useState({ kite: { brand: '', size: '' }, board: { brand: '', size: '' } });
  const [notes, setNotes] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    api.get('/spots').then(({ data }) => setSpots(data)).catch(() => {});
    api.get('/users/me/quota').then(({ data }) => setQuota(data)).catch(() => {});
  }, []);

  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith('video/') && !/\.(mp4|mov|m4v|webm)$/i.test(f.name)) {
      toast.error(t('upload.errChooseVideo')); return;
    }
    if (f.size > 500 * 1024 * 1024) { toast.error(t('upload.errMaxSize')); return; }
    setFile(f);
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); };

  const submit = async () => {
    if (!file) { toast.error(t('upload.errChooseFirst')); return; }
    if (quota && quota.credits <= 0) {
      toast.error(t('upload.errOutOfCredits')); navigate('/pricing'); return;
    }

    const windKn = wind ? windToKnots(wind, units) : null;
    const equipment = composeEquipment(gear);
    const spot = spotId ? spots.find((x) => x.id === spotId) : null;

    setUploading(true); setProgress(0);
    try {
      // 1) Initialise a chunked upload session (validates credits + file up-front).
      const { data: init } = await api.post('/analyses/upload/init', {
        filename: file.name,
        total_size: file.size,
        content_type: file.type || 'video/mp4',
        spot_id: spotId || null,
        spot_name: spot ? spot.name : null,
        wind_knots: windKn != null ? Number(windKn) : null,
        wind_direction: dir || null,
        equipment: equipment || null,
        notes: notes || null,
        skill_hint: user?.skill_level || null,
        lang: (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2),
      });
      const uploadId = init.upload_id;
      const chunkSize = init.chunk_size || CHUNK_SIZE;

      // 2) Upload the file in ordered chunks with real progress.
      const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const blob = file.slice(start, Math.min(start + chunkSize, file.size));
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', String(i));
        fd.append('chunk', blob, 'chunk');
        await api.post('/analyses/upload/chunk', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const loaded = start + (e.loaded || 0);
            setProgress(Math.min(99, Math.round((loaded / file.size) * 100)));
          },
        });
      }

      // 3) Finalise: assembles chunks, charges a credit, starts AI analysis.
      setProgress(100);
      const { data } = await api.post('/analyses/upload/complete', { upload_id: uploadId });
      toast.success(t('upload.toastComplete'));
      refreshUser();
      navigate(`/analysis/${data.id}`);
    } catch (err) {
      const detail = apiError(err, t('upload.toastFailed'));
      toast.error(detail);
      if (err.response?.status === 402) navigate('/pricing');
      setUploading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-[#48CAE4] mb-2">{t('upload.newSession')}</div>
          <h1 className="font-display text-4xl sm:text-5xl">{t('upload.title')}</h1>
          <p className="text-muted-foreground mt-2">{t('upload.subtitle')}</p>
        </div>
        {quota && (
          <div className="kite-card px-4 py-3 text-sm flex items-center gap-3" data-testid="upload-credits-card">
            <div>
              {quota.is_pro && <span className="text-[#FF6B35] font-semibold mr-2">{t('common.pro')}</span>}
              <span className="text-white font-data font-semibold" data-testid="upload-credits-count">{quota.credits}</span>
              <span className="text-muted-foreground"> {t('upload.creditsSuffix', { count: quota.credits })}</span>
            </div>
            <button onClick={() => navigate('/pricing')} className="text-[#48CAE4] hover:text-[#00B4D8] text-xs font-semibold underline underline-offset-2" data-testid="upload-buy-credits-link">{t('upload.getMore')}</button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-5 gap-6 mt-8">
        {/* Dropzone */}
        <div className="lg:col-span-3">
          {!file ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              data-testid="upload-dropzone"
              className={`cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${drag ? 'border-[#00B4D8] bg-[#00B4D8]/5' : 'border-white/15 bg-[#0E1114] hover:border-[#48CAE4]/50'}`}>
              <div className="w-16 h-16 rounded-2xl bg-[#00B4D8]/12 flex items-center justify-center mx-auto">
                <UploadCloud className="w-8 h-8 text-[#00B4D8]" />
              </div>
              <p className="text-lg font-semibold mt-5">{t('upload.dropTitle')}</p>
              <p className="text-muted-foreground text-sm mt-1">{t('upload.dropSub')}</p>
              <input ref={inputRef} type="file" accept="video/*" hidden onChange={(e) => pick(e.target.files?.[0])} data-testid="upload-file-input" />
            </div>
          ) : (
            <div className="kite-card p-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#48CAE4]/12 flex items-center justify-center"><Film className="w-5 h-5 text-[#48CAE4]" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground font-data">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                </div>
                {!uploading && <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-white" data-testid="upload-clear"><X className="w-5 h-5" /></button>}
              </div>
              {uploading && (
                <div className="mt-5">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1.5"><span>{progress >= 100 ? t('upload.finalizing') : t('upload.uploading')}</span><span className="font-data">{progress}%</span></div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}
            </div>
          )}

          {quota && quota.credits <= 0 && (
            <div className="mt-4 rounded-xl border border-[#FF6B35]/40 bg-[#FF6B35]/5 p-4 flex items-start gap-3" data-testid="upload-no-credits-alert">
              <AlertCircle className="w-5 h-5 text-[#FF6B35] shrink-0" />
              <div className="text-sm">{t('upload.noCreditsText')} <button onClick={() => navigate('/pricing')} className="text-[#FF6B35] font-semibold underline">{t('upload.noCreditsCta')}</button> {t('upload.noCreditsSuffix')}</div>
            </div>
          )}
        </div>

        {/* Session details */}
        <div className="lg:col-span-2 kite-card p-5 space-y-4">
          <h3 className="font-semibold flex items-center gap-2"><Wind className="w-4 h-4 text-[#00B4D8]" /> {t('upload.sessionDetails')} <span className="text-xs text-muted-foreground">{t('upload.optional')}</span></h3>
          <div>
            <Label>{t('upload.spot')}</Label>
            <Select value={spotId} onValueChange={setSpotId}>
              <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="upload-spot-select"><SelectValue placeholder={t('upload.spotPlaceholder')} /></SelectTrigger>
              <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-64">
                {spots.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.country}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('upload.wind', { unit: windUnit(units) })}</Label><Input type="number" value={wind} onChange={(e) => setWind(e.target.value)} placeholder={windPlaceholder(units)} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="upload-wind" /></div>
            <div>
              <Label>{t('upload.direction')}</Label>
              <Select value={dir} onValueChange={setDir}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="upload-dir"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-64">{DIRS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">{t('upload.equipment')}</Label>
            <EquipmentPicker savedGear={user?.equipment || []} value={gear} onChange={setGear} />
          </div>
          <div><Label>{t('upload.notes')}</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('upload.notesPlaceholder')} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="upload-notes" /></div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={submit} disabled={!file || uploading} data-testid="upload-submit"
          className="h-12 px-8 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl">
          {uploading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t('upload.uploading')}</> : <><Sparkles className="w-5 h-5 mr-2" /> {t('upload.analyzeWithAi')}</>}
        </Button>
      </div>
    </div>
  );
}
